import { useCallback, useMemo } from "react";

import { formatAwsDateString, formatCurrency } from "@covertree/utils";

import {
  PolicyListItemFragment,
  PolicyStatus,
} from "@covertree/agent-portal-graphql";
import { formatProductTypeName } from "@utils/formatPolicyType";
import { mapPolicyStatus } from "@utils/mapPolicyStatus";
import { useOpenPolicyDetails } from "@hooks/actions/useOpenPolicyDetails";

export const useMappedPolicies = (
  policies: PolicyListItemFragment[],
  sendEsignReminder: (policyLocator: string) => void
) => {
  const openPolicyDetails = useOpenPolicyDetails();

  const canSendEsignReminder = useCallback(
    (policy: PolicyListItemFragment) =>
      [
        PolicyStatus.PendingEsignRequired,
        PolicyStatus.PendingCancellationEsignRequired,
      ].includes(policy?.status) &&
      !(policy.isAgentsSignResponsibility || policy.physicallyMailDocuments),
    []
  );

  return useMemo(() => {
    if (!policies) {
      return [];
    }
    return policies.map((policy) => {
      const { previousAddress: address } = policy;
      return {
        id: policy.policyLocator,
        data: [
          policy.policyLocator,
          `${policy.policyholder.firstName} ${policy.policyholder.lastName}`,
          [
            address.streetAddress,
            address.city,
            address.state,
            address.zipCode,
          ].join(" "),
          formatProductTypeName(policy.productTypeName),
          formatCurrency(policy.units[0].pricing.grossPremium),
          mapPolicyStatus(policy.status),
          formatAwsDateString(policy.issuedDate),
          formatAwsDateString(policy.startDate),
          policy.agencyInformation.agentName,
          policy.agencyInformation.agencyName,
        ],
        onClickRow: () => openPolicyDetails(policy.policyLocator),
        actionMenuItems: [
          {
            label: "View details",
            onClick: () => openPolicyDetails(policy.policyLocator),
          },
          ...(canSendEsignReminder(policy)
            ? [
                {
                  label: "Send esign reminder",
                  onClick: () => sendEsignReminder(policy.policyLocator),
                },
              ]
            : []),
        ],
      };
    });
  }, [policies, sendEsignReminder]);
};
